import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Button, Spinner, Modal, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getChecklistListById, saveChecklistData, updateChecklistData } from "../../redux/actions/ChecklistActions";
import FormInput from "../FormInput";
import { logout } from "../../redux/actions/AuthActions";
import { toast } from "react-toastify";
import Select from "react-dropdown-select";
import swal from 'sweetalert2';
import Preloader from "../Preloader";
import '../../styles/common.css'
import {
  getClaimTypeCategoryOptions,
  getDefaultClaimCategory,
} from "../../constants/claimTypeCategory";

const AddChecklist = ({ show, onHide, editId }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const dispatch = useDispatch();
  const {
    AuthReducer: { auth, loading, errors },
    checklistReducer: {
      loadingChecklist,
      errorsChecklist,
      checklistLists,
      changedChecklistStatus,
    },
  } = useSelector((state) => state);

  const [validated, setValidated] = useState(false);
  const typeOptions = useMemo(() => getClaimTypeCategoryOptions(), []);
  const showTypeField = typeOptions.length > 1;
  const hasSingleTypeOption = !showTypeField;
  const singleTypeValue = getDefaultClaimCategory();
  const typeDropdownDisabled = !!editId;
  const addFormInitializedRef = useRef(false);

  useEffect(() => {
    if (!show) {
      addFormInitializedRef.current = false;
      return;
    }

    if (!editId) {
      if (addFormInitializedRef.current) return;
      addFormInitializedRef.current = true;
      setFormData({
        name: "",
        fee_amount: "",
        category_type: hasSingleTypeOption ? singleTypeValue : null,
      });
      return;
    }

    addFormInitializedRef.current = false;
    dispatch(getChecklistListById(auth?.token, editId));
  }, [
    show,
    auth?.token,
    editId,
    dispatch,
    hasSingleTypeOption,
    singleTypeValue,
  ]);

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);

    const payload = {
      ...formData,
      category_type: formData.category_type ?? getDefaultClaimCategory(),
    };
    if (!payload.category_type) {
      toast.error(t("Please select Type"));
      return;
    }

    if (editId) {
      dispatch(updateChecklistData(auth?.token, payload, editId));
    } else {
      dispatch(saveChecklistData(auth?.token, payload));
    }
  };

  useEffect(() => {
    if (!show || !editId) return;
    if (
      checklistLists &&
      !Array.isArray(checklistLists) &&
      checklistLists.id === editId
    ) {
      setFormData({
        name: checklistLists?.name ?? "",
        fee_amount: checklistLists?.fee_amount ?? "",
        category_type: checklistLists?.category_type ?? getDefaultClaimCategory(),
      });
    }
  }, [checklistLists, editId, show]);

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0 && errors?.status === 401) {
      dispatch(logout());
      toast.error(errors?.data?.message);
      onHide();
    }
  }, [errors, dispatch, onHide]);

  const inputHandler = (e) => {
    const { value, name } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (changedChecklistStatus?.success) {
      onHide();
      swal.fire({
        show: true,
        title: "Well done!",
        text: changedChecklistStatus?.message,
        icon: "success",
        showConfirmButton: false,
        timer: 1500
      });
      dispatch({ type: 'RESET_DELETE_CHECKLIST_STATUS' });
    }
  }, [changedChecklistStatus, dispatch, onHide]);

  return (
    <Modal show={show} onHide={onHide} size="md" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{(editId && checklistLists && Object.keys(checklistLists).length > 0) ? t("Update Checklist") : t("Add Checklist")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorsChecklist?.data?.message && (
          <div className="alert alert-danger">
            {errorsChecklist?.data?.message}
          </div>
        )}
        <Row>
          {showTypeField && (
            <Col lg={12}>
              <Form.Group className="mb-3">
                <Form.Label>{t("Type")}</Form.Label>
                <Select
                  key={`checklist-type-${show}-${editId ?? "add"}`}
                  options={typeOptions}
                  values={
                    formData.category_type
                      ? typeOptions.filter((opt) => opt.value === formData.category_type)
                      : []
                  }
                  name="category_type"
                  labelField="label"
                  valueField="value"
                  searchBy="label"
                  placeholder={t("Select Type")}
                  disabled={typeDropdownDisabled}
                  onChange={(selectedData) => {
                    setFormData((prev) => ({
                      ...prev,
                      category_type: selectedData?.length ? selectedData[0].value : null,
                    }));
                  }}
                />
                {errorsChecklist?.data?.category_type && (
                  <div className="invalid-feedback d-block">
                    <p className="text-danger">{errorsChecklist?.data?.category_type}</p>
                  </div>
                )}
              </Form.Group>
            </Col>
          )}

          {!showTypeField && errorsChecklist?.data?.category_type && (
            <Col lg={12}>
              <div className="invalid-feedback d-block mb-3">
                <p className="text-danger">{errorsChecklist?.data?.category_type}</p>
              </div>
            </Col>
          )}

          <Col lg={12}>
            <FormInput
              label={t("Checklist Name")}
              type="text"
              errors={errorsChecklist?.data}
              value={formData.name ?? ""}
              name="name"
              required={true}
              onChange={inputHandler}
              containerClass={"mb-3"}
              disabled={!!editId}
            />
          </Col>

          <Col lg={12}>
            <FormInput
              label={t("Fee Amount (INR)")}
              type="number"
              errors={errorsChecklist?.data}
              value={formData.fee_amount ?? ""}
              name="fee_amount"
              required={true}
              onChange={inputHandler}
              containerClass={"mb-3"}
            />
          </Col>
        </Row>
        <div className="d-flex justify-content-center">
          {loading ?
            <Button color="primary" disabled>
              <Spinner animation="border" size="sm" />
              {t("Please wait ...")}
            </Button>
            :
            <Button type="button" onClick={(e) => handleSubmit(e)} disabled={loading}>
              {(editId && checklistLists && Object.keys(checklistLists).length > 0) ? t("Update") : t("Add")}
            </Button>
          }
        </div>
      </Modal.Body>
      {loadingChecklist && <Preloader />}
    </Modal>
  );
};

export default AddChecklist;
